import { lookAhead } from "../../../engine/lookahead";
import { mainBoard } from "../../../engine/ranking";
import { displayName } from "../../../engine/selectors";
import type { Lineup, PlayerId, TournamentState } from "../../../engine/types";
import { useTournament, type Screen } from "../lib/useTournament";
import "./Print.css";

/** How many rounds ahead go on paper. */
const ROUNDS = 3;

const CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

const RULES = [
  "First to 11, win by 2.",
  "At ten minutes the leader wins.",
  "Level at time means one more point.",
  "Write the final score only.",
];

function nameOf(s: TournamentState, id: PlayerId): string {
  return displayName(s.players[id], "admin");
}

function pairText(s: TournamentState, pair: readonly PlayerId[]): string {
  return pair.map((id) => nameOf(s, id)).join(" & ");
}

/**
 * The paper fallback, for when the screens are gone: the next rounds as they would be drawn, with
 * boxes for the scores, and the leaderboard as it stands. Opened with the admin's link, read only.
 */
export function PrintView({ screen }: { screen: Screen }) {
  const t = useTournament(screen);
  const s = t.state;
  const back = `/a/${screen.token}${location.search}`;
  if (!s) {
    return (
      <div className="print">
        <p className="print-wait">Connecting to the tournament.</p>
      </div>
    );
  }
  const rounds = lookAhead(s, ROUNDS);
  const firstNo = s.rounds.length + 1;
  const printed = (
    <p className="print-stamp">
      Printed at {CLOCK.format(t.now())}. These are the next rounds if nobody arrives, leaves or takes a break.
    </p>
  );
  const why =
    s.mode === "mexicano" ? (
      <p className="print-why">
        {rounds.length ? "Only this round can be promised. " : "No round can be promised yet. "}
        In Mexicano, later rounds follow the leaderboard, so they are drawn once the scores are in.
      </p>
    ) : null;
  const board = mainBoard(s);
  // two columns of names once one would run off the sheet
  const split = board.length > 26 ? Math.ceil(board.length / 2) : board.length;
  return (
    <div className="print">
      <nav className="print-bar">
        <a href={back}>Back to admin</a>
        <button type="button" onClick={() => window.print()}>
          Print
        </button>
      </nav>
      {rounds.map((lineup, i) => (
        <section className="sheet" key={i}>
          {i === 0 ? printed : null}
          <h1>Round {firstNo + i}</h1>
          {i === 0 ? why : null}
          <RoundSheet s={s} lineup={lineup} />
        </section>
      ))}
      <section className="sheet">
        {rounds.length ? null : printed}
        {rounds.length ? null : why}
        <h1>Leaderboard</h1>
        <p className="print-sub">{s.rounds.length ? `After round ${s.rounds.length}` : "Before round 1"}</p>
        <div className="print-board">
          {[board.slice(0, split), board.slice(split)].map((rows, i) =>
            rows.length ? (
              <table key={i}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Games</th>
                    <th>Average</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.rank}</td>
                      <td>{nameOf(s, r.id)}</td>
                      <td>{r.games}</td>
                      <td>{r.avg.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null,
          )}
        </div>
        <h2>Scoring</h2>
        <ol className="print-rules">
          {RULES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/** One round: each court a row with a box beside each pair, the resting list beneath. */
function RoundSheet({ s, lineup }: { s: TournamentState; lineup: Lineup }) {
  return (
    <>
      <ol className="print-courts">
        {lineup.courts.map((c) => (
          <li key={c.court}>
            <div className="print-no">
              <span>Court</span>
              <b>{c.court}</b>
            </div>
            <div className="print-pairs">
              <p>{pairText(s, c.a)}</p>
              <span>v</span>
              <p>{pairText(s, c.b)}</p>
            </div>
            <div className="print-boxes">
              <i />
              <i />
            </div>
          </li>
        ))}
      </ol>
      <h2>Resting</h2>
      <p className="print-rest">{lineup.sittingOut.length ? lineup.sittingOut.map((id) => nameOf(s, id)).join(", ") : "Nobody"}</p>
    </>
  );
}
